import { useState, useEffect, useCallback } from 'react'
import { brandApi, merchantApi } from '../api'
import { Pill } from './Pill'
import { ErrorState, errorMessage } from './ErrorState'
import { BrandAuthImport } from './BrandAuthImport'
import { SidePanel } from '../ui/ui'
import { enumLabel, useI18n, useT } from '../i18n'
import type { MonitoredBrand, MerchantBrandAuthRow, MerchantRow, BrandAuthStatus } from '../types'

const STATUS_VARIANT: Record<BrandAuthStatus, 'ok' | 'warning' | 'critical' | 'neutral'> = {
  ACTIVE: 'ok',
  SUSPENDED: 'warning',
  PENDING: 'neutral',
  REVOKED: 'critical',
}

const AUTH_STATUSES: BrandAuthStatus[] = ['ACTIVE', 'SUSPENDED', 'PENDING', 'REVOKED']

export function BrandsManager() {
  const { t, fmt } = useI18n()
  const [brands, setBrands] = useState<MonitoredBrand[]>([])
  const [editing, setEditing] = useState<MonitoredBrand | null>(null)
  const [creating, setCreating] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [scanningId, setScanningId] = useState<string | null>(null)
  const [scanResult, setScanResult] = useState<{ brandId: string; message: string; type: 'ok' | 'info' | 'error' } | null>(null)
  /** Brand dont on affiche la section marchands autorises */
  const [expandedBrandId, setExpandedBrandId] = useState<string | null>(null)
  const [showAuthImport, setShowAuthImport] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      setBrands(await brandApi.findAll())
      setError(null)
    } catch (err: unknown) {
      setBrands([])
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { refresh() }, [refresh])

  // Auto-clear scan result after 20s (longer so user sees it)
  useEffect(() => {
    if (!scanResult) return
    const t = setTimeout(() => setScanResult(null), 20_000)
    return () => clearTimeout(t)
  }, [scanResult])

  const handleToggle = async (id: string) => {
    await brandApi.toggle(id)
    refresh()
  }

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(t('brands.deleteConfirm', { name }))) return
    await brandApi.remove(id)
    refresh()
  }

  const handleScan = async (id: string, name: string) => {
    setScanningId(id)
    setScanResult(null)
    try {
      const result = await brandApi.scan(id)
      if (result.total > 0) {
        setScanResult({
          brandId: id,
          message: t('brands.scanFound', { total: result.total, name, saved: result.saved }),
          type: 'ok',
        })
      } else {
        setScanResult({
          brandId: id,
          message: t('brands.scanNone', { name }),
          type: 'info',
        })
      }
      refresh() // Refresh to update lastScannedAt
    } catch (err: unknown) {
      setScanResult({
        brandId: id,
        message: t('brands.scanError', { name, error: errorMessage(err) }),
        type: 'error',
      })
    } finally {
      setScanningId(null)
    }
  }

  return (
    <div>
      {/* ── Header ──────────────────────────────────── */}
      <div className="brands-header">
        <div>
          <h2>{t('brands.title')}</h2>
          <p className="brands-subtitle">{t('brands.subtitle')}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-secondary" onClick={() => setShowAuthImport(true)}>
            {t('brands.importAuths')}
          </button>
          <button className="btn-primary" onClick={() => { setCreating(true); setEditing(null) }}>
            {t('brands.newBrand')}
          </button>
        </div>
      </div>

      {showAuthImport && (
        <BrandAuthImport
          onClose={() => setShowAuthImport(false)}
          onImported={refresh}
        />
      )}

      {/* ── Formulaire ──────────────────────────────── */}
      {(creating || editing) && (
        <BrandForm
          key={editing ? `edit-${editing.id}` : 'create'}
          brand={editing}
          onSave={() => { setCreating(false); setEditing(null); refresh() }}
          onCancel={() => { setCreating(false); setEditing(null) }}
        />
      )}

      {error && <ErrorState message={error} onRetry={refresh} />}

      {!error && !loading && brands.length === 0 && (
        <div className="table-empty">{t('brands.empty')}</div>
      )}

      {/* ── Liste ────────────────────────────────────── */}
      <div className="brands-grid">
        {brands.map((b) => (
          <div key={b.id} className={`brand-card ${!b.isActive ? 'brand-inactive' : ''}`}>
            <div className="brand-card-header">
              <div className="brand-name-row">
                {b.displayColor && (
                  <span className="brand-dot" style={{ background: b.displayColor }} />
                )}
                <strong>{b.name}</strong>
                <Pill
                  label={enumLabel(t, 'brands.category', b.category, b.category)}
                  title={b.category}
                  variant={b.category === 'INTERNAL' ? 'info' : b.category === 'PARTNER' ? 'ok' : 'neutral'}
                />
                {!b.isActive && <Pill label={t('brands.disabled')} variant="neutral" />}
              </div>
              {b.description && <div className="brand-desc">{b.description}</div>}
            </div>

            <div className="brand-section">
              <div className="brand-section-title">{t('brands.searchTerms')}</div>
              <div className="brand-tags">
                {b.searchTerms.map((t) => <span key={t} className="brand-tag">{t}</span>)}
              </div>
            </div>

            {b.customTyposquats.length > 0 && (
              <div className="brand-section">
                <div className="brand-section-title">{t('brands.customTyposquats')}</div>
                <div className="brand-tags">
                  {b.customTyposquats.map((t) => <span key={t} className="brand-tag brand-tag-typo">{t}</span>)}
                </div>
              </div>
            )}

            {b.paymentSignatures && b.paymentSignatures.length > 0 && (
              <div className="brand-section">
                <div className="brand-section-title">{t('brands.paymentSignatures')}</div>
                <div className="brand-tags">
                  {b.paymentSignatures.map((s) => <span key={s} className="brand-tag brand-tag-payment">{s}</span>)}
                </div>
              </div>
            )}

            <div className="brand-section">
              <div className="brand-section-title">{t('brands.officialDomains')}</div>
              <div className="brand-tags">
                {b.officialDomains.map((d) => <span key={d} className="brand-tag brand-tag-domain">{d}</span>)}
                {b.officialDomains.length === 0 && <span className="td-muted">{t('common.none')}</span>}
              </div>
            </div>

            {b.searchPatterns?.length > 0 && (
              <div className="brand-section">
                <div className="brand-section-title">{t('brands.webPatterns')}</div>
                <div className="brand-tags">
                  {b.searchPatterns.map((p) => (
                    <span key={p} className="brand-tag brand-tag-pattern">
                      {p.split(/(\{brand\}|\{country\})/i).map((part, i) =>
                        /^\{(brand|country)\}$/i.test(part) ? <mark key={i}>{part}</mark> : part,
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {b.targetCountries.length > 0 && (
              <div className="brand-section">
                <div className="brand-section-title">{t('brands.targetCountries')}</div>
                <span className="td-muted">{b.targetCountries.join(', ')}</span>
              </div>
            )}

            {/* ── Section Marchands autorisés ──────────── */}
            <BrandMerchantsSection
              brandId={b.id}
              brandName={b.name}
              expanded={expandedBrandId === b.id}
              onToggle={() => setExpandedBrandId(expandedBrandId === b.id ? null : b.id)}
            />

            {/* Indicateur de scan en cours */}
            {scanningId === b.id && (
              <div className="scan-result scan-result-info">
                {t('brands.scanningFor', { terms: b.searchTerms.join(', ') })}
              </div>
            )}

            {/* Resultat de scan pour cette marque */}
            {scanResult && scanResult.brandId === b.id && scanningId !== b.id && (
              <div className={`scan-result scan-result-${scanResult.type}`}>
                {scanResult.message}
              </div>
            )}

            <div className="brand-footer">
              <span className="td-muted">
                {t('brands.lastScan', { date: b.lastScannedAt ? fmt.dateTime(b.lastScannedAt) : t('common.never') })}
              </span>
              <div className="brand-actions">
                <button
                  className={`btn-scan${scanningId === b.id ? ' scanning' : ''}`}
                  onClick={() => handleScan(b.id, b.name)}
                  disabled={scanningId === b.id || !b.isActive}
                  title={!b.isActive ? t('brands.scanDisabledHint') : t('brands.scanHint')}
                >
                  {scanningId === b.id ? t('brands.scanning') : t('brands.scanBtn')}
                </button>
                <button onClick={() => { setEditing(b); setCreating(false) }}>{t('common.edit')}</button>
                <button onClick={() => handleToggle(b.id)}>
                  {b.isActive ? t('brands.disable') : t('brands.enable')}
                </button>
                <button className="btn-danger" onClick={() => handleDelete(b.id, b.name)}>
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!loading && brands.length === 0 && (
        <div className="table-empty">{t('brands.emptyHint')}</div>
      )}
    </div>
  )
}

// ─── Section Marchands autorisés par marque ──────────────────────────────────

interface BrandMerchantsSectionProps {
  brandId: string
  brandName: string
  expanded: boolean
  onToggle: () => void
}

function BrandMerchantsSection({ brandId, brandName, expanded, onToggle }: BrandMerchantsSectionProps) {
  const t = useT()
  const [auths, setAuths] = useState<MerchantBrandAuthRow[]>([])
  const [count, setCount] = useState<number | null>(null)
  const [loadingAuths, setLoadingAuths] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [allMerchants, setAllMerchants] = useState<MerchantRow[]>([])
  const [merchantSearch, setMerchantSearch] = useState('')
  const [selectedMerchantId, setSelectedMerchantId] = useState('')
  const [contractRef, setContractRef] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** ID de l'autorisation en cours de modification */
  const [editingAuthId, setEditingAuthId] = useState<string | null>(null)
  const [editStatus, setEditStatus] = useState<BrandAuthStatus>('ACTIVE')
  const [editContract, setEditContract] = useState('')

  // Charger le comptage initial (non expande)
  useEffect(() => {
    brandApi.getMerchants(brandId)
      .then((data) => setCount(data.length))
      .catch(() => setCount(0))
  }, [brandId])

  // Charger les autorisations quand on expanse
  useEffect(() => {
    if (!expanded) return
    loadAuths()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, brandId])

  const loadAuths = async () => {
    setLoadingAuths(true)
    try {
      const data = await brandApi.getMerchants(brandId)
      setAuths(data)
      setCount(data.length)
    } catch {
      setAuths([])
    } finally {
      setLoadingAuths(false)
    }
  }

  const handleAdd = async () => {
    if (!selectedMerchantId) return
    setSaving(true)
    setError(null)
    try {
      await brandApi.addMerchant(brandId, {
        merchantId: selectedMerchantId,
        contractRef: contractRef.trim() || undefined,
      })
      setShowAddForm(false)
      setSelectedMerchantId('')
      setContractRef('')
      setMerchantSearch('')
      await loadAuths()
    } catch (err: unknown) {
      setError(errorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async (merchantId: string, merchantName: string) => {
    if (!confirm(t('brands.revokeConfirm', { merchant: merchantName, brand: brandName }))) return
    try {
      await brandApi.removeMerchant(brandId, merchantId)
      await loadAuths()
    } catch (err: unknown) {
      setError(errorMessage(err))
    }
  }

  const handleStatusChange = async (merchantId: string) => {
    try {
      await brandApi.updateMerchantAuth(brandId, merchantId, {
        status: editStatus,
        contractRef: editContract.trim() || undefined,
      })
      setEditingAuthId(null)
      await loadAuths()
    } catch (err: unknown) {
      setError(errorMessage(err))
    }
  }

  const openAddForm = async () => {
    setShowAddForm(true)
    setError(null)
    try {
      const merchants = await merchantApi.findAll()
      setAllMerchants(merchants)
    } catch {
      setAllMerchants([])
    }
  }

  // Filtrer les marchands pour l'autocomplete (exclure ceux deja autorises)
  const authorizedIds = new Set(auths.map((a) => a.merchantId))
  const filteredMerchants = allMerchants
    .filter((m) => !authorizedIds.has(m.id))
    .filter((m) =>
      !merchantSearch.trim() ||
      m.name.toLowerCase().includes(merchantSearch.toLowerCase()) ||
      m.url.toLowerCase().includes(merchantSearch.toLowerCase()),
    )

  return (
    <div className="brand-section brand-merchants-section">
      <div
        className="brand-section-title brand-merchants-toggle"
        onClick={onToggle}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <span>{expanded ? '▼' : '▶'} {t('brands.authorizedMerchants')}</span>
        {count !== null && (
          <span className="brand-merchants-count">({count})</span>
        )}
      </div>

      {expanded && (
        <div className="brand-merchants-content">
          {/* Bouton Ajouter */}
          {!showAddForm && (
            <button
              className="btn-primary btn-sm"
              onClick={openAddForm}
              style={{ marginBottom: 8 }}
            >
              {t('brands.addMerchant')}
            </button>
          )}

          {/* Formulaire d'ajout */}
          {showAddForm && (
            <div className="brand-merchant-add-form">
              <div className="form-field">
                <label>{t('brands.findMerchant')}</label>
                <input
                  type="text"
                  value={merchantSearch}
                  onChange={(e) => setMerchantSearch(e.target.value)}
                  placeholder={t('brands.findMerchantPlaceholder')}
                  autoFocus
                />
              </div>

              {filteredMerchants.length > 0 && (
                <div className="merchant-autocomplete">
                  {filteredMerchants.slice(0, 10).map((m) => (
                    <div
                      key={m.id}
                      className={`merchant-autocomplete-item ${selectedMerchantId === m.id ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedMerchantId(m.id)
                        setMerchantSearch(m.name)
                      }}
                    >
                      <strong>{m.name}</strong>
                      <span className="td-muted" style={{ marginLeft: 8, fontSize: '0.85em' }}>
                        {m.url} — {m.country}
                      </span>
                    </div>
                  ))}
                  {filteredMerchants.length > 10 && (
                    <div className="td-muted" style={{ padding: '4px 8px', fontSize: '0.85em' }}>
                      {t('brands.andMore', { n: filteredMerchants.length - 10 })}
                    </div>
                  )}
                </div>
              )}

              {merchantSearch.trim() && filteredMerchants.length === 0 && (
                <div className="td-muted" style={{ padding: '4px 0', fontSize: '0.85em' }}>
                  {t('brands.noMerchantMatch')}
                </div>
              )}

              <div className="form-field" style={{ marginTop: 8 }}>
                <label>{t('brands.contractRef')}</label>
                <input
                  type="text"
                  value={contractRef}
                  onChange={(e) => setContractRef(e.target.value)}
                  placeholder={t('brands.contractPlaceholder')}
                />
              </div>

              {error && <div className="form-error">{error}</div>}

              <div className="form-actions" style={{ marginTop: 8 }}>
                <button onClick={() => { setShowAddForm(false); setError(null); setMerchantSearch(''); setSelectedMerchantId('') }}>
                  {t('common.cancel')}
                </button>
                <button
                  className="btn-primary"
                  onClick={handleAdd}
                  disabled={!selectedMerchantId || saving}
                >
                  {saving ? t('brands.authorizing') : t('brands.authorize')}
                </button>
              </div>
            </div>
          )}

          {/* Liste des marchands autorises */}
          {loadingAuths ? (
            <div className="td-muted">{t('common.loading')}</div>
          ) : auths.length === 0 ? (
            <div className="td-muted" style={{ padding: '8px 0' }}>
              {t('brands.noAuthorized')}
            </div>
          ) : (
            <div className="brand-merchants-list">
              {auths.map((auth) => {
                const merchantName = auth.merchant?.name ?? auth.merchantId
                const merchantUrl = auth.merchant?.url ?? ''
                const isEditing = editingAuthId === auth.id

                return (
                  <div key={auth.id} className="brand-merchant-row">
                    {isEditing ? (
                      /* Mode edition inline */
                      <div className="brand-merchant-edit">
                        <div className="brand-merchant-edit-header">
                          <strong>{merchantName}</strong>
                          <span className="td-muted">{merchantUrl}</span>
                        </div>
                        <div className="brand-merchant-edit-fields">
                          <select
                            value={editStatus}
                            onChange={(e) => setEditStatus(e.target.value as BrandAuthStatus)}
                          >
                            {AUTH_STATUSES.map((s) => (
                              <option key={s} value={s}>{t(`brands.authStatus.${s}` as const)}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={editContract}
                            onChange={(e) => setEditContract(e.target.value)}
                            placeholder={t('brands.contractRefShort')}
                            style={{ flex: 1 }}
                          />
                          <button
                            className="btn-primary btn-sm"
                            onClick={() => handleStatusChange(auth.merchantId)}
                          >
                            OK
                          </button>
                          <button
                            className="btn-sm"
                            onClick={() => setEditingAuthId(null)}
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Mode affichage */
                      <>
                        <div className="brand-merchant-info">
                          <span className="brand-merchant-name">{merchantName}</span>
                          {merchantUrl && (
                            <span className="brand-merchant-url td-muted">{merchantUrl}</span>
                          )}
                        </div>
                        <div className="brand-merchant-meta">
                          <Pill
                            label={enumLabel(t, 'brands.authStatus', auth.status, auth.status)}
                            title={auth.status}
                            variant={STATUS_VARIANT[auth.status]}
                          />
                          {auth.contractRef && (
                            <span className="brand-merchant-contract">{auth.contractRef}</span>
                          )}
                        </div>
                        <div className="brand-merchant-actions">
                          <button
                            className="btn-sm"
                            onClick={() => {
                              setEditingAuthId(auth.id)
                              setEditStatus(auth.status)
                              setEditContract(auth.contractRef ?? '')
                            }}
                            title={t('brands.editAuth')}
                          >
                            {t('common.edit')}
                          </button>
                          <button
                            className="btn-sm btn-danger"
                            onClick={() => handleRemove(auth.merchantId, merchantName)}
                            title={t('brands.revokeTitle')}
                          >
                            {t('brands.revoke')}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Formulaire creation / edition ────────────────────────────────────────────

interface FormProps {
  brand: MonitoredBrand | null
  onSave: () => void
  onCancel: () => void
}

function BrandForm({ brand, onSave, onCancel }: FormProps) {
  const t = useT()
  const isEditing = !!brand

  const [name, setName]                     = useState(brand?.name ?? '')
  const [description, setDescription]       = useState(brand?.description ?? '')
  const [searchTermsRaw, setSearchTermsRaw] = useState(brand?.searchTerms.join(', ') ?? '')
  const [officialRaw, setOfficialRaw]       = useState(brand?.officialDomains.join(', ') ?? '')
  const [typosquatsRaw, setTyposquatsRaw]   = useState(brand?.customTyposquats.join(', ') ?? '')
  const [paymentSigsRaw, setPaymentSigsRaw] = useState(brand?.paymentSignatures?.join(', ') ?? '')
  const [countriesRaw, setCountriesRaw]     = useState(brand?.targetCountries.join(', ') ?? '')
  const [patternsRaw, setPatternsRaw]       = useState(
    (brand?.searchPatterns ?? ['payer avec {brand}', 'paiement {brand}', '{brand} payment']).join('\n'),
  )
  const [category, setCategory]             = useState(brand?.category ?? 'CLIENT')
  // Couleur persistée en base et rendue par un <input type="color"> : une
  // variable CSS n'y a pas sa place, il faut une valeur hexadécimale littérale.
  const [displayColor, setDisplayColor]     = useState(brand?.displayColor ?? '#3F4E63')
  const [saving, setSaving]                 = useState(false)
  const [error, setError]                   = useState<string | null>(null)

  const splitComma = (s: string) =>
    s.split(',').map((v) => v.trim().toLowerCase()).filter((v) => v.length > 0)

  const handleSubmit = async () => {
    setError(null)
    const terms = splitComma(searchTermsRaw)

    if (!name.trim()) { setError(t('brands.nameRequired')); return }
    if (terms.length === 0) { setError(t('brands.termsRequired')); return }

    setSaving(true)
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        searchTerms: terms,
        officialDomains: splitComma(officialRaw),
        customTyposquats: splitComma(typosquatsRaw),
        paymentSignatures: splitComma(paymentSigsRaw),
        targetCountries: splitComma(countriesRaw).map((c) => c.toUpperCase()),
        // Une formulation par ligne : ce sont des phrases, elles peuvent contenir des virgules
        searchPatterns: patternsRaw.split('\n').map((p) => p.trim()).filter((p) => p.length > 0),
        category: category as 'INTERNAL' | 'PARTNER' | 'CLIENT',
        displayColor,
      }

      if (isEditing) {
        await brandApi.update(brand.id, payload)
      } else {
        await brandApi.create(payload)
      }
      onSave()
    } catch (err: unknown) {
      setError(errorMessage(err) || t('brands.saveError'))
    } finally {
      setSaving(false)
    }
  }

  // Le formulaire s'ouvre en fenêtre, comme les autres écrans de configuration :
  // affiché en tête de page, il repoussait la liste des marques hors de l'écran,
  // si bien qu'on ne voyait plus ce qu'on était en train de modifier.
  return (
    <SidePanel
      title={isEditing ? t('brands.formEdit', { name: brand.name }) : t('brands.formNew')}
      onClose={onCancel}
      wide
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? t('brands.formSaving') : isEditing ? t('common.save') : t('brands.formCreate')}
          </button>
        </>
      }
    >
      {error && <div className="form-error">{error}</div>}

      <div className="form-grid">
        <div className="form-field">
          <label>{t('brands.fieldName')}</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('brands.namePlaceholder')} />
        </div>

        <div className="form-field">
          <label>{t('brands.fieldCategory')}</label>
          <select value={category} onChange={(e) => setCategory(e.target.value as 'INTERNAL' | 'PARTNER' | 'CLIENT')}>
            <option value="INTERNAL">{t('brands.catInternal')}</option>
            <option value="PARTNER">{t('brands.catPartner')}</option>
            <option value="CLIENT">{t('brands.catClient')}</option>
          </select>
        </div>

        <div className="form-field">
          <label>{t('brands.fieldColor')}</label>
          <input type="color" value={displayColor} onChange={(e) => setDisplayColor(e.target.value)} />
        </div>

        <div className="form-field form-field-wide">
          <label>{t('brands.fieldDescription')}</label>
          <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('brands.descriptionPlaceholder')} />
        </div>

        <div className="form-field form-field-wide">
          <label>{t('brands.fieldTerms')}</label>
          <input
            value={searchTermsRaw}
            onChange={(e) => setSearchTermsRaw(e.target.value)}
            placeholder={t('brands.termsPlaceholder')}
          />
          <span className="form-hint">{t('brands.termsHint')}</span>
        </div>

        <div className="form-field form-field-wide">
          <label>{t('brands.fieldOfficial')}</label>
          <input
            value={officialRaw}
            onChange={(e) => setOfficialRaw(e.target.value)}
            placeholder={t('brands.officialPlaceholder')}
          />
          <span className="form-hint">{t('brands.officialHint')}</span>
        </div>

        <div className="form-field form-field-wide">
          <label>{t('brands.fieldTyposquats')}</label>
          <input
            value={typosquatsRaw}
            onChange={(e) => setTyposquatsRaw(e.target.value)}
            placeholder={t('brands.typosquatsPlaceholder')}
          />
          <span className="form-hint">{t('brands.typosquatsHint')}</span>
        </div>

        <div className="form-field form-field-wide">
          <label>{t('brands.fieldSignatures')}</label>
          <input
            value={paymentSigsRaw}
            onChange={(e) => setPaymentSigsRaw(e.target.value)}
            placeholder={t('brands.signaturesPlaceholder')}
          />
          <span className="form-hint">{t('brands.signaturesHint')}</span>
        </div>

        <div className="form-field form-field-wide">
          <label htmlFor="brand-patterns">{t('brands.fieldPatterns')}</label>
          <textarea
            id="brand-patterns"
            rows={5}
            value={patternsRaw}
            onChange={(e) => setPatternsRaw(e.target.value)}
            placeholder={'payer avec {brand}\npaiement {brand} {country}'}
          />
          {/* Les jetons passent en paramètres : écrits dans le catalogue, ils
              seraient pris pour des trous à remplir. */}
          <span className="form-hint">
            {t('brands.patternsHint', { brandToken: '{brand}', countryToken: '{country}' })}
          </span>
          {!patternsRaw.includes('{country}') && (
            <button
              type="button"
              className="btn-link"
              onClick={() =>
                setPatternsRaw((prev) =>
                  [prev.trim(), 'payer avec {brand} {country}', 'paiement {brand} {country}'].filter(Boolean).join('\n'),
                )
              }
            >
              {t('brands.addCountryPatterns')}
            </button>
          )}
          {patternsRaw.trim() && (
            <span className="form-hint">
              {t('brands.patternsPreview', {
                examples: patternsRaw
                  .split('\n')
                  .map((p) => p.trim())
                  .filter(Boolean)
                  .slice(0, 4)
                  .map(
                    (p) =>
                      '« ' +
                      p
                        .replace(/\{brand\}/gi, name.trim() || t('brands.patternsPreviewBrand'))
                        .replace(/\{country\}/gi, t('brands.patternsPreviewCountry')) +
                      ' »',
                  )
                  .join(', '),
              })}
            </span>
          )}
        </div>

        <div className="form-field form-field-wide">
          <label>{t('brands.fieldCountries')}</label>
          <input
            value={countriesRaw}
            onChange={(e) => setCountriesRaw(e.target.value)}
            placeholder={t('brands.countriesPlaceholder')}
          />
        </div>
      </div>
    </SidePanel>
  )
}
